// src/modules/stock/StockReport.js

import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useSubCollection from '../../hooks/useSubCollection';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination
} from '../../components/common';
import { exportInventory } from '../../documents/exportInventory';
import styles from '../Stock/Logistics.module.css';

const CURRENT_STOCK_COLLECTION = 'current-stock';
const REPORT_HISTORY_COLLECTION = 'report-history';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const PRODUCTS_COLLECTION = 'products';
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const LOCATIONS_COLLECTION = 'localizacoes';

// Configuração da Água
const WATER_MATERIALS = ['MP001', 'AGUA001', 'WATER001'];

const isWaterMaterial = (material) => {
    if (!material) return false;
    const code = material.code || material.rawMaterialCode || '';
    return WATER_MATERIALS.includes(code.toUpperCase());
};

// Componente Principal
const StockReportPage = () => {
    const can = usePagePermissions('estoque_atual');
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Estado dos filtros
    const [filters, setFilters] = useState({
        productTypeId: '',
        categoryId: '',
        locationId: '',
        stockStatus: 'all',
        minQuantity: '',
        maxQuantity: '',
        searchTerm: '',
        includeWater: false,
        includeLocations: true
    });

    // Hooks para dados auxiliares
    const { data: allRawMaterials, loading: loadingRawMaterials } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');
    const { data: products, loading: loadingProducts } = useFirestore(PRODUCTS_COLLECTION, 'code');
    const { data: productTypes, loading: loadingTypes } = useSubCollection(
        PRODUCT_TYPE_COLLECTION_PATH.parentCollection,
        PRODUCT_TYPE_COLLECTION_PATH.parentId,
        PRODUCT_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );
    const { data: categories, loading: loadingCategories } = useFirestore(CATEGORY_COLLECTION, 'order');
    const [locations, setLocations] = useState([]);
    const [currentStock, setCurrentStock] = useState([]);

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Carregar localizações
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const snap = await getDocs(collection(db, LOCATIONS_COLLECTION));
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                list.sort((a, b) => a.id.localeCompare(b.id));
                setLocations(list);
            } catch (error) {
                console.error("Erro ao carregar localizações:", error);
            }
        };
        fetchLocations();
    }, []);

    // Carregar estoque atual
    useEffect(() => {
        const fetchStock = async () => {
            try {
                const q = query(
                    collection(db, CURRENT_STOCK_COLLECTION),
                    orderBy('rawMaterialCode')
                );
                const snapshot = await getDocs(q);
                const stockItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCurrentStock(stockItems);
            } catch (error) {
                console.error("Erro ao carregar estoque:", error);
                addToast('Erro ao carregar dados de estoque', 'error');
            }
        };
        fetchStock();
    }, []);

    // Carregar histórico de relatórios
    useEffect(() => {
        loadReports();
    }, []);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Erro ao carregar histórico:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Erro ao excluir:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Combinar matérias-primas e produtos para mapeamento
    const allItems = useMemo(() => {
        const combined = [];
        if (allRawMaterials) {
            combined.push(...allRawMaterials.map(item => ({ ...item, isProduct: false })));
        }
        if (products) {
            combined.push(...products.map(item => ({ ...item, isProduct: true })));
        }
        return combined;
    }, [allRawMaterials, products]);

    // Mapeamento de itens para propriedades
    const itemMap = useMemo(() => {
        const map = new Map();
        allItems.forEach(item => {
            map.set(item.id, {
                productTypeIds: item.productTypeIds || [],
                categoryIds: item.categoryIds || [],
                code: item.code,
                name: item.commercialName || item.name,
                isProduct: item.isProduct || false
            });
        });
        return map;
    }, [allItems]);

    // Agrupar estoque por material (combinando localizações)
    const groupedStock = useMemo(() => {
        const stockMap = new Map();

        currentStock.forEach(item => {
            const key = item.rawMaterialId;

            if (!stockMap.has(key)) {
                stockMap.set(key, {
                    rawMaterialId: item.rawMaterialId,
                    rawMaterialCode: item.rawMaterialCode,
                    rawMaterialName: item.rawMaterialName,
                    totalQuantity: 0,
                    locations: [],
                    isWater: isWaterMaterial(item)
                });
            }

            const group = stockMap.get(key);
            group.totalQuantity += item.quantity;
            group.locations.push({
                id: item.locationId,
                description: item.locationDescription,
                quantity: item.quantity,
                stockId: item.id
            });
        });

        for (const group of stockMap.values()) {
            group.locations.sort((a, b) => a.id.localeCompare(b.id));
        }

        return Array.from(stockMap.values()).sort((a, b) =>
            (a.rawMaterialCode || '').localeCompare(b.rawMaterialCode || '')
        );
    }, [currentStock]);

    // Aplicar filtros
    const filteredStock = useMemo(() => {
        let filtered = [...groupedStock];

        if (!filters.includeWater) {
            filtered = filtered.filter(item => !item.isWater);
        }

        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                (item.rawMaterialCode || '').toLowerCase().includes(term) ||
                (item.rawMaterialName || '').toLowerCase().includes(term) ||
                item.locations.some(loc =>
                    loc.id.toLowerCase().includes(term) ||
                    (loc.description || '').toLowerCase().includes(term)
                )
            );
        }

        if (filters.productTypeId) {
            filtered = filtered.filter(item => {
                if (item.isWater) return false;
                const props = itemMap.get(item.rawMaterialId);
                return props?.productTypeIds?.includes(filters.productTypeId) || false;
            });
        }

        if (filters.categoryId) {
            filtered = filtered.filter(item => {
                if (item.isWater) return false;
                const props = itemMap.get(item.rawMaterialId);
                return props?.categoryIds?.includes(filters.categoryId) || false;
            });
        }

        if (filters.locationId) {
            filtered = filtered.filter(item =>
                item.locations.some(loc => loc.id === filters.locationId)
            );
        }

        if (filters.stockStatus !== 'all') {
            filtered = filtered.filter(item => {
                if (item.isWater) return false;
                switch (filters.stockStatus) {
                    case 'low':
                        return item.totalQuantity < 500 && item.totalQuantity >= 100;
                    case 'critical':
                        return item.totalQuantity < 100 && item.totalQuantity > 0;
                    case 'normal':
                        return item.totalQuantity >= 500;
                    case 'zero':
                        return item.totalQuantity === 0;
                    default:
                        return true;
                }
            });
        }

        if (filters.minQuantity && filters.minQuantity !== '') {
            const min = parseFloat(filters.minQuantity);
            if (!isNaN(min)) {
                filtered = filtered.filter(item => item.totalQuantity >= min);
            }
        }

        if (filters.maxQuantity && filters.maxQuantity !== '') {
            const max = parseFloat(filters.maxQuantity);
            if (!isNaN(max)) {
                filtered = filtered.filter(item => item.totalQuantity <= max);
            }
        }

        return filtered;
    }, [groupedStock, filters, itemMap]);

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredStock.length === 0) {
            addToast('Nenhum item encontrado com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const exportItems = filteredStock.map(item => ({
                code: item.rawMaterialCode,
                name: item.rawMaterialName,
                quantity: item.totalQuantity,
                locations: item.locations.map(loc => `${loc.id}`).join(', '),
                locationDetails: item.locations.map(loc => `${loc.id}: ${loc.quantity.toFixed(3)}`).join('; ')
            }));

            const filterDisplay = {
                includeLocations: filters.includeLocations,
                includeWater: filters.includeWater
            };

            if (filters.productTypeId && productTypes) {
                const type = productTypes.find(t => t.id === filters.productTypeId);
                if (type) filterDisplay.productTypeName = type.name;
            }
            if (filters.categoryId && categories) {
                const cat = categories.find(c => c.id === filters.categoryId);
                if (cat) filterDisplay.categoryName = cat.name;
            }
            if (filters.locationId && locations) {
                const loc = locations.find(l => l.id === filters.locationId);
                if (loc) filterDisplay.locationName = `${loc.id} - ${loc.description}`;
            }
            if (filters.stockStatus !== 'all') {
                filterDisplay.stockStatus = filters.stockStatus;
            }
            if (filters.minQuantity) filterDisplay.minQuantity = filters.minQuantity;
            if (filters.maxQuantity) filterDisplay.maxQuantity = filters.maxQuantity;
            if (filters.searchTerm) filterDisplay.searchTerm = filters.searchTerm;

            let reportTitle = 'RELATÓRIO DE ESTOQUE';
            if (filterDisplay.productTypeName) reportTitle += ` - ${filterDisplay.productTypeName}`;
            if (filterDisplay.categoryName) reportTitle += ` - ${filterDisplay.categoryName}`;

            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.productTypeName) filterParts.push(filterDisplay.productTypeName.replace(/\s/g, '_'));
            if (filterDisplay.categoryName) filterParts.push(filterDisplay.categoryName.replace(/\s/g, '_'));
            if (filterDisplay.stockStatus && filterDisplay.stockStatus !== 'all') {
                const statusMap = { low: 'Baixo', critical: 'Critico', normal: 'Normal', zero: 'Zero' };
                filterParts.push(statusMap[filterDisplay.stockStatus] || filterDisplay.stockStatus);
            }
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Relatorio_Estoque${filterSuffix}_${timestamp}.xlsx`;

            await exportInventory({
                items: exportItems,
                filters: filterDisplay,
                reportTitle: reportTitle,
                fileName: fileName,
                showNotification: (msg, type) => addToast(msg, type)
            });

            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                itemCount: filteredStock.length,
                filters: {
                    productTypeId: filters.productTypeId,
                    productTypeName: filterDisplay.productTypeName || null,
                    categoryId: filters.categoryId,
                    categoryName: filterDisplay.categoryName || null,
                    locationId: filters.locationId,
                    locationName: filterDisplay.locationName || null,
                    stockStatus: filters.stockStatus,
                    minQuantity: filters.minQuantity || null,
                    maxQuantity: filters.maxQuantity || null,
                    searchTerm: filters.searchTerm || null,
                    includeWater: filters.includeWater,
                    includeLocations: filters.includeLocations
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Erro ao gerar relatório:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            productTypeId: '',
            categoryId: '',
            locationId: '',
            stockStatus: 'all',
            minQuantity: '',
            maxQuantity: '',
            searchTerm: '',
            includeWater: false,
            includeLocations: true
        });
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFilterDescription = (filters) => {
        const parts = [];
        if (filters.productTypeName) parts.push(`Tipo: ${filters.productTypeName}`);
        if (filters.categoryName) parts.push(`Cat: ${filters.categoryName}`);
        if (filters.locationName) parts.push(`Local: ${filters.locationName}`);
        if (filters.stockStatus && filters.stockStatus !== 'all') {
            const statusMap = { low: 'Baixo', critical: 'Crítico', normal: 'Normal', zero: 'Zero' };
            parts.push(`Status: ${statusMap[filters.stockStatus]}`);
        }
        if (filters.minQuantity) parts.push(`Mín: ${filters.minQuantity}`);
        if (filters.maxQuantity) parts.push(`Máx: ${filters.maxQuantity}`);
        if (filters.searchTerm) parts.push(`Busca: ${filters.searchTerm}`);
        if (!filters.includeWater) parts.push('Sem Água');
        if (parts.length === 0) return 'Todos os itens';
        return parts.join(', ');
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = loading || loadingRawMaterials || loadingProducts || loadingTypes || loadingCategories;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            <div className={styles.filterRow}>
                <div className={styles.inputGroup}>
                    <label>Tipo de Produto</label>
                    <select
                        value={filters.productTypeId}
                        onChange={(e) => handleFilterChange('productTypeId', e.target.value)}
                        className={styles.select}
                        disabled={isLoading}
                    >
                        <option value="">Todos os Tipos</option>
                        {productTypes?.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.inputGroup}>
                    <label>Categoria</label>
                    <select
                        value={filters.categoryId}
                        onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                        className={styles.select}
                        disabled={isLoading}
                    >
                        <option value="">Todas as Categorias</option>
                        {categories?.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className={styles.filterRow}>
                <div className={styles.inputGroup}>
                    <label>Localização</label>
                    <select
                        value={filters.locationId}
                        onChange={(e) => handleFilterChange('locationId', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todas as Localizações</option>
                        {locations?.map(loc => (
                            <option key={loc.id} value={loc.id}>
                                {loc.id} - {loc.description}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.inputGroup}>
                    <label>Status do Estoque</label>
                    <select
                        value={filters.stockStatus}
                        onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
                        className={styles.select}
                    >
                        <option value="all">Todos</option>
                        <option value="low">Estoque Baixo (&lt; 500)</option>
                        <option value="critical">Estoque Crítico (&lt; 100)</option>
                        <option value="normal">Estoque Normal (≥ 500)</option>
                        <option value="zero">Sem Estoque</option>
                    </select>
                </div>
            </div>

            <div className={styles.filterRow}>
                <div className={styles.inputGroup}>
                    <label>Quantidade Mínima</label>
                    <input
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="Ex: 100"
                        value={filters.minQuantity}
                        onChange={(e) => handleFilterChange('minQuantity', e.target.value)}
                        className={styles.input}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <label>Quantidade Máxima</label>
                    <input
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="Ex: 10000"
                        value={filters.maxQuantity}
                        onChange={(e) => handleFilterChange('maxQuantity', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            <div className={styles.filterRow}>
                <div className={styles.inputGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={filters.includeLocations}
                            onChange={(e) => handleFilterChange('includeLocations', e.target.checked)}
                        />
                        Incluir detalhes de localização no relatório
                    </label>
                </div>
            </div>

            <div className={styles.previewInfo} style={{ marginTop: '16px' }}>
                📊 {filteredStock.length} {filteredStock.length === 1 ? 'item encontrado' : 'itens encontrados'} com os filtros atuais
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Estoque"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredStock.length} itens)`}
                cancelText="Cancelar"
                maxWidth="800px"
            >
                {renderFilterForm()}
                <div className={styles.modalActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Relatório de Estoque"
                actionText="Novo Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading || !can.generate}
            />

            {/* Report History */}
            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>
                    Histórico de Relatórios Gerados
                </h3>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '160px' }}>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th style={{ width: '200px' }}>Gerado por</th>
                                <th style={{ width: '80px' }}>Itens</th>
                                <th style={{ width: '250px' }}>Filtros Aplicados</th>
                                <th style={{ width: '80px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className={styles.loadingCell}>
                                        <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                        <span style={{ marginTop: '8px', display: 'block' }}>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Novo Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontWeight: 500 }}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{report.itemCount}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDeleteClick(report.id)}
                                                    className={styles.deleteButton}
                                                    style={{ padding: '4px 12px', fontSize: '12px' }}
                                                >
                                                    Excluir
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StockReportPage;